
import { useState } from 'react'
import { toast } from 'sonner'
import SiteFrame from '@/components/landing/SiteFrame'
import { Container, Eyebrow, GlassCard, FeatureCard, Stagger, GradientButton, GhostButton } from '@/components/landing/design-system'
import { Rocket, Building2, Cpu, Users, ShieldCheck, Zap, Loader2 } from 'lucide-react'

export default function GetStartedPage() {
  const [form, setForm] = useState({
    kind: 'launch', name: '', email: '', company: '', domain: '',
    tenants_expected: '', regions: '', timeline: '', budget_band: '',
    notes: '',
  })
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))

  const submit = async (e) => {
    e.preventDefault()
    if (!form.name || !form.email || !form.company) return toast.error('Name, work email, and company are required.')
    setBusy(true)
    try {
      const r = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(form),
      })
      const j = await r.json()
      if (!r.ok) throw new Error(j.error || 'submission failed')
      setSent(true)
      toast.success('Application received. Kick-off call coming your way.')
    } catch (err) {
      toast.error(err.message || 'Something went wrong')
    } finally { setBusy(false) }
  }

  return (
    <SiteFrame>
      <section className="relative pt-32 md:pt-40 pb-14 overflow-hidden">
        <div className="pointer-events-none absolute inset-0 -z-10 opacity-60"
             style={{ background: 'radial-gradient(55% 45% at 50% 0%, rgba(255,193,7,0.18), transparent 70%)' }} />
        <Container>
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <Eyebrow>Get Started</Eyebrow>
            <h1 className="text-4xl md:text-6xl font-semibold tracking-tight text-white leading-[1.02]">
              Launch your platform. Fast.
            </h1>
            <p className="text-zinc-400 md:text-lg leading-relaxed">
              Answer a few questions and we’ll spin up a dedicated launch plan.
              Every serious operator is paired with a solutions engineer inside
              one business day.
            </p>
          </div>
        </Container>
      </section>

      <section className="pb-8">
        <Container>
          <Stagger gap={80} className="grid gap-3 md:grid-cols-4">
            <FeatureCard icon={Rocket}     title="Live in weeks"     body="Not months. Real production, not a sandbox." />
            <FeatureCard icon={Building2}  title="Your brand"        body="Custom domain, tokens, emails — all yours." tone="green" />
            <FeatureCard icon={Cpu}        title="AI included"       body="Mentor, auto-docs, and copilots on day one." />
            <FeatureCard icon={ShieldCheck} title="Compliance ready" body="Audit trail, RBAC, and geo-fencing baked in." tone="green" />
          </Stagger>
        </Container>
      </section>

      <section className="pb-24">
        <Container size="narrow">
          <GlassCard className="p-6 md:p-10">
            {sent ? (
              <div className="py-14 text-center space-y-4">
                <div className="text-2xl font-semibold text-white">Application received</div>
                <div className="text-zinc-400 text-sm max-w-md mx-auto">
                  A launch specialist will reach out within one business day
                  with a scoping call invite and a preliminary launch plan.
                </div>
                <div className="flex items-center justify-center gap-2">
                  <GradientButton href="/" size="md">Back to Kalki Ai Cloud</GradientButton>
                  <GhostButton    href="/platform" size="md">Explore Platform</GhostButton>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5">
                <div className="flex items-center gap-2">
                  <div className="h-9 w-9 rounded-lg border border-brand-gold/25 bg-brand-gold/10 text-brand-gold flex items-center justify-center"><Zap className="h-4 w-4" /></div>
                  <div>
                    <div className="text-sm font-semibold text-white">Tell us about your launch</div>
                    <div className="text-[12px] text-zinc-500">All fields except notes are required for a same-day response.</div>
                  </div>
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  <Field label="Full name"  value={form.name}    onChange={set('name')}    required />
                  <Field label="Work email" value={form.email}   onChange={set('email')}   required type="email" />
                  <Field label="Company"    value={form.company} onChange={set('company')} required />
                  <Field label="Preferred domain" value={form.domain} onChange={set('domain')} placeholder="platform.yourbrand.com" />
                  <Field label="Expected tenants" value={form.tenants_expected} onChange={set('tenants_expected')} placeholder="e.g. 5 in year 1" />
                  <Field label="Regions"    value={form.regions} onChange={set('regions')}  placeholder="e.g. UAE, India" />
                  <Field label="Target timeline"   value={form.timeline}    onChange={set('timeline')}    placeholder="e.g. Q4 launch" />
                  <Field label="Budget band"       value={form.budget_band} onChange={set('budget_band')} placeholder="e.g. $30k-$100k/yr" />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Notes</label>
                  <textarea rows={4} value={form.notes} onChange={set('notes')}
                    className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-brand-gold/50 focus:ring-2 focus:ring-brand-gold/20" />
                </div>
                <button
                  type="submit"
                  disabled={busy}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-brand-gold to-[#FFE17A] text-black font-semibold py-3 text-[15px] shadow-[0_10px_40px_-10px_rgba(255,193,7,0.55)] hover:shadow-[0_14px_46px_-8px_rgba(255,193,7,0.75)] transition-all disabled:opacity-60"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Users className="h-4 w-4" />Submit application</>)}
                </button>
                <div className="text-[11px] text-zinc-500 text-center">Submissions are triaged by a real human, not an autoresponder.</div>
              </form>
            )}
          </GlassCard>
        </Container>
      </section>
    </SiteFrame>
  )
}

const Field = ({ label, value, onChange, type = 'text', required = false, placeholder = '' }) => (
  <label className="flex flex-col gap-1">
    <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">{label}{required && <span className="text-brand-gold">*</span>}</span>
    <input type={type} value={value} onChange={onChange} required={required} placeholder={placeholder}
      className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-brand-gold/50 focus:ring-2 focus:ring-brand-gold/20" />
  </label>
)
